/**
 * Type-preserving wrappers around `Object.keys` / `values` / `entries`.
 *
 * The native methods widen their results because, at runtime, an object can
 * carry keys beyond those its type declares:
 *   - `Object.keys(o)`    -> `string[]`            (loses `keyof T`)
 *   - `Object.values(o)`  -> `any[]`               (loses the value union)
 *   - `Object.entries(o)` -> `[string, any][]`     (loses both)
 * That widening is what forces `as keyof T` casts or trips TS7053 implicit-any
 * index errors at call sites. These wrappers return `keyof`-typed results so
 * callers stop re-writing that cast (and mis-writing it) at every call site.
 *
 * Behaviour matches the hand-written `Object.keys(o) as Array<keyof T>` cast
 * exactly — same assumption that the object has its declared keys, just in one
 * place instead of scattered. The generic is bound to `object`, so primitives
 * and null are caught at compile time. See `typedObject.demo.md` for the cases.
 */
// `Object.keys/values/entries` only enumerate string keys at runtime, so the
// key type is `Extract<keyof T, string>` (not `keyof T`, which can also include
// `number`/`symbol`) to stay sound for objects with numeric or symbol keys.
export const TypedObject = {
  keys: <T extends object>(o: T): Array<Extract<keyof T, string>> =>
    Object.keys(o) as Array<Extract<keyof T, string>>,
  values: <T extends object>(o: T): Array<T[Extract<keyof T, string>]> =>
    Object.values(o) as Array<T[Extract<keyof T, string>]>,
  entries: <T extends object>(o: T): Array<[Extract<keyof T, string>, T[Extract<keyof T, string>]]> =>
    Object.entries(o) as Array<[Extract<keyof T, string>, T[Extract<keyof T, string>]]>,
}
